import { GameInfo } from '@/types';

import { LobbyAction } from './LobbySessionActions';
import { ControllerUserDto } from '@/controller/models/ControllerUserModel';
import { HosterGlobalSettings } from '@toilet-paper-games/core';

export enum NetworkDataType {
  REFRESH = 'REFRESH',
  DEBUG = 'DEBUG',
  GAME_ACTION = 'GAME_ACTION',
  UPDATE_USER_NAME = 'UPDATE_USER_NAME',
  LOBBY_ACTION = 'LOBBY_ACTION',
  START_GAME = 'START_GAME',
  END_GAME = 'END_GAME',
  /** Fetches session info an also notes Controller User is network ready */
  FETCH_SESSION_INFO_C2H = 'FETCH_SESSION_INFO_C2H',
  SESSION_INFO_H2C = 'SESSION_INFO_H2C',
  GAME_READY_STATUS_HOSTER_H2C = 'GAME_READY_STATUS_HOSTER_H2C',
  GAME_READY_STATUS_CONTROLLER_C2H = 'GAME_READY_STATUS_CONTROLLER_C2H',
  PING_H2C = 'PING_H2C',
  PONG_C2H = 'PONG_C2H',
  PONG_H2C = 'PONG_H2C',

  UPDATE_HOSTER_SETTINGS_C2H = 'UPDATE_HOSTER_SETTINGS_C2H',

  HEARTBEAT_H2C = 'HEARTBEAT_H2C',
  HEARTBEAT_RETURN_C2H = 'HEARTBEAT_RETURN_C2H',
}

export interface DataTransferStructure {
  type: NetworkDataType;
  data: unknown;
}

export interface RefreshTransfer extends DataTransferStructure {
  type: NetworkDataType.REFRESH;
  data: unknown;
}

export interface StartGameTransfer extends DataTransferStructure {
  type: NetworkDataType.START_GAME;
  data: { game: GameInfo };
}

export interface EndGameTransfer extends DataTransferStructure {
  type: NetworkDataType.END_GAME;
  data: unknown;
}

export interface GameActionTransfer extends DataTransferStructure {
  type: NetworkDataType.GAME_ACTION;
  data: unknown;
}

export interface LogTransfer extends DataTransferStructure {
  type: NetworkDataType.DEBUG;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
}

export interface UpdateUserTransfer extends DataTransferStructure {
  type: NetworkDataType.UPDATE_USER_NAME;
  data: {
    screenName: string;
  };
}

export interface LobbyActionTransfer extends DataTransferStructure {
  type: NetworkDataType.LOBBY_ACTION;
  data: {
    action: LobbyAction;
  };
}

export interface FetchSessionInfoTransferC2H extends DataTransferStructure {
  type: NetworkDataType.FETCH_SESSION_INFO_C2H;
  data: {};
}

export interface SessionInfoTransferH2C extends DataTransferStructure {
  type: NetworkDataType.SESSION_INFO_H2C;
  data: {
    user: ControllerUserDto;
    connectedUsers: ControllerUserDto[];

    devMode: boolean;
    devGameUrl: string | null;
    forceShowExitButton: boolean;
    showHiddenGames: boolean;
    selectedGame: GameInfo | null;
    isPlayingGame: boolean;

    hosterGlobalSettings: HosterGlobalSettings | null;
  };
}

export interface GameReadyStatusHosterTransfer extends DataTransferStructure {
  type: NetworkDataType.GAME_READY_STATUS_HOSTER_H2C;
  data: {
    ready: boolean;
  };
}

export interface GameReadyStatusControllerTransfer extends DataTransferStructure {
  type: NetworkDataType.GAME_READY_STATUS_CONTROLLER_C2H;
  data: {
    ready: boolean;
  };
}

export interface PingTransferHoster extends DataTransferStructure {
  type: NetworkDataType.PING_H2C;
  data: {
    id: string;
    userId: string;
    timeSinceStart: number;
    hosterTime: number;
  };
}

export interface PongTransferController extends DataTransferStructure {
  type: NetworkDataType.PONG_C2H;
  data: {
    id: string;
    userId: string;
    controllerTime: number;
  };
}

export interface PongTransferHoster extends DataTransferStructure {
  type: NetworkDataType.PONG_H2C;
  data: {
    id: string;
    userId: string;
    pingMs: number;
    timeSinceStart: number;
    hosterTime: number;
  };
}

export type UpdateHosterSettingsC2H = {
  [K in keyof HosterGlobalSettings]: {
    type: NetworkDataType.UPDATE_HOSTER_SETTINGS_C2H;
    data: {
      key: K;
      value: HosterGlobalSettings[K];
    };
  };
}[keyof HosterGlobalSettings];

export interface HeartbeatTransferH2C extends DataTransferStructure {
  type: NetworkDataType.HEARTBEAT_H2C;
  data: {
    messageId: string;
  };
}

export interface HeartbeatReturnTransferC2H extends DataTransferStructure {
  type: NetworkDataType.HEARTBEAT_RETURN_C2H;
  data: {
    messageId: string;
  };
}

export type NetworkDataTransfer =
  | EndGameTransfer
  | StartGameTransfer
  | RefreshTransfer
  | GameActionTransfer
  | LogTransfer
  | UpdateUserTransfer
  | LobbyActionTransfer
  | FetchSessionInfoTransferC2H
  | SessionInfoTransferH2C
  | GameReadyStatusHosterTransfer
  | GameReadyStatusControllerTransfer
  | PingTransferHoster
  | PongTransferController
  | PongTransferHoster
  | UpdateHosterSettingsC2H
  | HeartbeatTransferH2C
  | HeartbeatReturnTransferC2H;

export type NetworkDataResponse = NetworkDataTransfer & { userId: string };

export function isNetworkDataTransfer(data: unknown): data is NetworkDataTransfer {
  // Check is not complete, but should be enough
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    (typeof data.type === 'number' || typeof data.type === 'string') &&
    'data' in data &&
    typeof data.data !== 'undefined'
  );
}

export function isNetworkDataResponse(data: unknown): data is NetworkDataResponse {
  return (
    isNetworkDataTransfer(data) && 'userId' in data && typeof data.userId === 'string'
  );
}
